import useDeviceModeFlg from "../../common/useDeviceModeFlg";
import Spinner from "../../common/Spinner";
import { EXPENSE_TYPE, INCOME_TYPE, type Category } from "../../common/commonConstant";
import { useMaster } from "../../common/MasterContext";
import type { RegularFormValues, RegularIncomeExpenseItem } from "./bop030w030";

interface BOP030W030TemplateProps {
  items: RegularIncomeExpenseItem[];
  isFormOpen: boolean;
  formValues: RegularFormValues;
  isLoading: boolean;
  onOpenAddForm: () => void;
  onOpenEditForm: (item: RegularIncomeExpenseItem) => void;
  onCancelForm: () => void;
  onFormTypeChange: (type: Category) => void;
  onFormCategoryLargeChange: (value: string) => void;
  onFormCategorySmallChange: (value: string) => void;
  onFormAmountChange: (value: string) => void;
  onFormMemoChange: (value: string) => void;
  onFormKeihiFlgChange: (checked: boolean) => void;
  onFormMikakuteiFlgChange: (checked: boolean) => void;
  onFormSubmit: () => void;
  onDelete: (item: RegularIncomeExpenseItem) => void;
}

const BOP030W030Template = ({
  items,
  isFormOpen,
  formValues,
  isLoading,
  onOpenAddForm,
  onOpenEditForm,
  onCancelForm,
  onFormTypeChange,
  onFormCategoryLargeChange,
  onFormCategorySmallChange,
  onFormAmountChange,
  onFormMemoChange,
  onFormKeihiFlgChange,
  onFormMikakuteiFlgChange,
  onFormSubmit,
  onDelete,
}: BOP030W030TemplateProps) => {
  const deviceModeFlg = useDeviceModeFlg();
  const { categoryLargeList, categorySmallList } = useMaster();

  return (
    <div
      className={`bop030w030-container ${
        deviceModeFlg ? "deviceMode-sp" : "deviceMode-pc"
      }`}
    >
      {isLoading && (
        <div className="bop030w030-loading-overlay">
          <Spinner label="処理中..." />
        </div>
      )}

      <div className="bop030w030-content">
        <h1 className="screen-title">定期収支管理</h1>

        <div className="category-section">
          <div className="section-header">
            <h2 className="section-title">定期収支一覧</h2>
            <button type="button" className="add-button" onClick={onOpenAddForm}>
              ＋ 新規追加
            </button>
          </div>

          {items.length === 0 ? (
            <div className="empty-row">定期収支が登録されていません</div>
          ) : deviceModeFlg ? (
            <div className="regular-card-list" role="list">
              {items.map((item) => (
                <article
                  key={item.serialNo}
                  className="regular-card"
                  role="listitem"
                  aria-label={`${item.categoryLarge} / ${item.categorySmall} ${item.amount.toLocaleString()}円`}
                >
                  <div className="regular-card-header">
                    <span className="row-type">{item.type}</span>
                    <span className="regular-card-amount">
                      {item.amount.toLocaleString()}円
                    </span>
                  </div>

                  <dl className="regular-card-grid">
                    <div>
                      <dt>項目（大）</dt>
                      <dd>{item.categoryLarge}</dd>
                    </div>
                    <div>
                      <dt>項目（小）</dt>
                      <dd>{item.categorySmall}</dd>
                    </div>
                    <div>
                      <dt>経費</dt>
                      <dd>{item.keihiFlg === 1 ? "対象" : ""}</dd>
                    </div>
                    <div>
                      <dt>未確定</dt>
                      <dd>{item.mikakuteiFlg === 1 ? "未確定" : ""}</dd>
                    </div>
                    <div className="regular-card-memo">
                      <dt>備考</dt>
                      <dd>{item.memo}</dd>
                    </div>
                  </dl>

                  <div className="regular-card-actions">
                    <button
                      type="button"
                      className="edit-button"
                      onClick={() => onOpenEditForm(item)}
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      className="row-delete-button"
                      onClick={() => onDelete(item)}
                    >
                      削除
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="regular-table">
              <div className="table-header">
                <div className="col-type">分類</div>
                <div className="col-category">項目（大）</div>
                <div className="col-category">項目（小）</div>
                <div className="col-amount">金額（円）</div>
                <div className="col-memo">備考</div>
                <div className="col-keihi">経費</div>
                <div className="col-keihi">未確定</div>
                <div className="col-actions"></div>
              </div>

              {items.map((item) => (
                <div key={item.serialNo} className="table-row">
                  <div className="col-type">{item.type}</div>
                  <div className="col-category">{item.categoryLarge}</div>
                  <div className="col-category">{item.categorySmall}</div>
                  <div className="col-amount">{item.amount.toLocaleString()}</div>
                  <div className="col-memo">{item.memo}</div>
                  <div className="col-keihi">{item.keihiFlg === 1 ? "対象" : ""}</div>
                  <div className="col-keihi">{item.mikakuteiFlg === 1 ? "未確定" : ""}</div>
                  <div className="col-actions">
                    <button
                      type="button"
                      className="edit-button"
                      onClick={() => onOpenEditForm(item)}
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      className="row-delete-button"
                      onClick={() => onDelete(item)}
                    >
                      削除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {isFormOpen && (
          <div
            className="master-form-overlay"
            role="dialog"
            aria-modal="true"
            aria-label={formValues.serialNo !== null ? "定期収支の編集" : "定期収支の追加"}
          >
            <div className="master-form-panel">
              <h2 className="section-title">
                {formValues.serialNo !== null ? "定期収支の編集" : "定期収支の追加"}
              </h2>

              <div className="form-row">
                <label className="form-label required-label">収支</label>
                <div className="radio-group">
                  <label className="radio-item">
                    <input
                      type="radio"
                      name="regularFormType"
                      checked={formValues.type === INCOME_TYPE}
                      onChange={() => onFormTypeChange(INCOME_TYPE)}
                    />
                    収入
                  </label>
                  <label className="radio-item">
                    <input
                      type="radio"
                      name="regularFormType"
                      checked={formValues.type === EXPENSE_TYPE}
                      onChange={() => onFormTypeChange(EXPENSE_TYPE)}
                    />
                    支出
                  </label>
                </div>
              </div>

              <div className="form-row">
                <label className="form-label required-label" htmlFor="regularFormCategoryLarge">
                  項目（大）
                </label>
                <select
                  id="regularFormCategoryLarge"
                  value={formValues.categoryLarge}
                  onChange={(e) => onFormCategoryLargeChange(e.target.value)}
                >
                  <option value="">選択してください</option>
                  {categoryLargeList
                    .filter((c) => c.type === formValues.type)
                    .map((c) => (
                      <option key={c.label} value={c.category}>
                        {c.label}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-row">
                <label className="form-label required-label" htmlFor="regularFormCategorySmall">
                  項目（小）
                </label>
                <select
                  id="regularFormCategorySmall"
                  value={formValues.categorySmall}
                  onChange={(e) => onFormCategorySmallChange(e.target.value)}
                >
                  <option value="">選択してください</option>
                  {categorySmallList
                    .filter((c) => c.category === formValues.categoryLarge)
                    .map((c) => (
                      <option key={c.label} value={c.label}>
                        {c.label}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-row">
                <label className="form-label required-label" htmlFor="regularFormAmount">
                  金額（円）
                </label>
                <input
                  id="regularFormAmount"
                  type="number"
                  min={0}
                  value={formValues.amount}
                  onChange={(e) => onFormAmountChange(e.target.value)}
                />
              </div>

              <div className="form-row">
                <label className="form-label" htmlFor="regularFormMemo">
                  備考
                </label>
                <textarea
                  id="regularFormMemo"
                  value={formValues.memo}
                  onChange={(e) => onFormMemoChange(e.target.value)}
                  className="memo-area"
                />
              </div>

              <div className="form-row">
                <label className="form-label">経費</label>
                <label className="toggle-switch" aria-label="経費トグル">
                  <input
                    type="checkbox"
                    checked={formValues.keihiFlg === 1}
                    onChange={(e) => onFormKeihiFlgChange(e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>

              <div className="form-row">
                <label className="form-label">未確定</label>
                <label className="toggle-switch" aria-label="未確定トグル">
                  <input
                    type="checkbox"
                    checked={formValues.mikakuteiFlg === 1}
                    onChange={(e) => onFormMikakuteiFlgChange(e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>

              <div className="master-form-actions">
                <button
                  type="button"
                  className="clear-button"
                  onClick={onCancelForm}
                  disabled={isLoading}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="register-button"
                  onClick={onFormSubmit}
                  disabled={isLoading}
                >
                  保存
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BOP030W030Template;
